import { FairResultDto } from '@/api/generated';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { parseUtc } from '@/views/admin/candidates/candidateStatus.ts';
import { COLORS, formatNumber, ORDINAL } from '@/views/admin/dashboard/dashboardTheme.ts';
import { FUNNEL_STEPS, type FunnelStep, shareOfAdded } from '@/views/admin/fairs/fairs.ts';
import React, { useEffect, useState } from 'react';
import { IoAddCircleOutline, IoCheckmarkCircle } from 'react-icons/io5';

// One step of the orange ramp per funnel step: the further down the funnel, the darker
const STEP_COLORS: Record<FunnelStep, string> = {
  added: ORDINAL[0],
  quizFinished: ORDINAL[3],
  recommended: ORDINAL[5],
  registered: ORDINAL[1],
};

interface Props {
  isLoading: boolean;
  language: string;
  onAdd: () => void;
  onSelect: (note: string | undefined) => void;
  results: FairResultDto[] | undefined;
  selected: string | undefined;
}

export const FairResultCards: React.FC<Props> = ({
  isLoading,
  language,
  onAdd,
  onSelect,
  results,
  selected,
}) => {
  const { t } = useTranslationWithPrefix('views.admin.fairs');
  // bars grow from zero once the results are in
  const [grown, setGrown] = useState(false);
  const hasResults = !!results?.length;
  useEffect(() => {
    if (!hasResults) {
      return;
    }
    const frame = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(frame);
  }, [hasResults]);

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map(index => (
          <div
            className="h-[270px] animate-pulse bg-white"
            key={index}
            style={{ border: '1px solid #e5e5e5' }}
          />
        ))}
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div
        className="flex flex-col items-center gap-3 bg-white px-6 py-10 text-center"
        style={{ border: '1px dashed #ccc' }}
      >
        <p className="m-0 max-w-[560px] text-sm" style={{ color: COLORS.inkSecondary }}>
          {t('results.empty')}
        </p>
        <button className="btn btn-primary" onClick={onAdd} type="button">
          <IoAddCircleOutline aria-hidden className="me-2" />
          {t('addCandidates')}
        </button>
      </div>
    );
  }

  const day = new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const dateTime = new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' });
  const percent = (value: number) =>
    new Intl.NumberFormat(language, {
      maximumFractionDigits: value > 0 && value < 0.1 ? 1 : 0,
      style: 'percent',
    }).format(value);

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {results.map(result => {
        const first = parseUtc(result.firstAddedAt) ?? new Date();
        const last = parseUtc(result.lastAddedAt) ?? first;
        const active = result.note !== undefined && result.note === selected;
        const content = (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3
                  className="m-0 truncate text-base font-bold"
                  style={{ color: result.note ? COLORS.ink : COLORS.inkMuted }}
                  title={result.note}
                >
                  {result.note ?? t('results.noName')}
                </h3>
                <div
                  className="mt-0.5 text-xs"
                  style={{ color: COLORS.inkSecondary }}
                  title={t('results.lastAdded', { date: dateTime.format(last) })}
                >
                  {day.formatRange(first, last)}
                </div>
              </div>
              {active && <IoCheckmarkCircle aria-hidden color={COLORS.orange} size={22} />}
            </div>

            <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span
                className="text-4xl leading-none font-bold"
                style={{ color: COLORS.ink, fontVariantNumeric: 'tabular-nums' }}
              >
                {percent(shareOfAdded(result, 'recommended'))}
              </span>
              <span className="text-sm" style={{ color: COLORS.inkSecondary }}>
                {t('results.recommendedShare')}
              </span>
            </div>

            <ol className="m-0 mt-4 flex list-none flex-col gap-2 p-0">
              {FUNNEL_STEPS.map(step => {
                const share = shareOfAdded(result, step);
                return (
                  <li key={step}>
                    <div className="flex items-baseline justify-between gap-2 text-xs">
                      <span style={{ color: COLORS.inkSecondary }}>
                        {t(`results.funnel.${step}`)}
                      </span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        <strong style={{ color: COLORS.ink }}>
                          {formatNumber(result[step], language)}
                        </strong>
                        {step !== 'added' && (
                          <span className="ms-2" style={{ color: COLORS.inkMuted }}>
                            {percent(share)}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="mt-1 h-2 w-full" style={{ backgroundColor: COLORS.grid }}>
                      <div
                        className="h-full"
                        style={{
                          backgroundColor: STEP_COLORS[step],
                          borderRadius: '0 4px 4px 0',
                          transition: 'width 700ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                          width: grown ? `${share * 100}%` : 0,
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        );
        // padding is inline: Boosted's .p-5 is !important and three times as wide
        const cardStyle: React.CSSProperties = {
          border: `1px solid ${active ? COLORS.orange : '#e5e5e5'}`,
          boxShadow: active ? `inset 0 0 0 1px ${COLORS.orange}` : undefined,
          padding: 20,
        };
        // the candidates added without a fair name cannot be listed on their own
        return result.note === undefined ? (
          <article className="min-w-0 bg-white" key="no-name" style={cardStyle}>
            {content}
          </article>
        ) : (
          <button
            aria-label={`${t('results.showCandidates')}: ${result.note}`}
            aria-pressed={active}
            className="flex min-w-0 cursor-pointer flex-col items-stretch justify-start bg-white text-left transition-shadow hover:shadow-md"
            key={result.note}
            onClick={() => onSelect(active ? undefined : result.note)}
            style={cardStyle}
            type="button"
          >
            {content}
          </button>
        );
      })}
    </div>
  );
};
